import { Button } from '@/components/Button';
import { useAuthStore } from '@/stores/useAuthStore';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const SubscriptionSuccess = () => {
  const { t, i18n } = useTranslation('subscription-success');
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Get the current subscription
  const currentSubscription = user?.subscriptions?.find(
    sub => sub.stripe_status === 'active' || sub.stripe_status === 'trialing',
  );

  const subscriptionType = currentSubscription?.type || 'plus';
  const isTrialing = currentSubscription?.stripe_status === 'trialing';

  // Determine plan name based on subscription type
  const getPlanName = () => {
    switch (subscriptionType.toLowerCase()) {
      case 'plus':
        return t('plans.business-plus.name');
      case 'pro':
        return t('plans.business-pro.name');
      default:
        return t('plans.business-plus.name');
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <motion.div
        className="flex flex-col items-center justify-center rounded-xl bg-white p-8 shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Success Icon */}
        <div className="mb-6 flex size-20 items-center justify-center rounded-full bg-green-100">
          <FaCheck className="size-10 text-green-500" />
        </div>

        {/* Success Message */}
        <h1 className="mb-2 text-center text-3xl font-bold text-gray-800">
          {t('title')}
        </h1>
        <p className="mb-6 text-center text-lg text-gray-600">
          {t('description', { plan: getPlanName() })}
        </p>

        {/* Subscription Details */}
        <div className="mb-8 w-full max-w-md rounded-lg bg-gray-50 p-6">
          <h3 className="mb-4 text-xl font-semibold text-gray-800">
            {t('details-title')}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('plan')}:</span>
              <span className="font-medium text-gray-800">{getPlanName()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('status')}:</span>
              <span className="font-medium text-green-600">
                {isTrialing ? t('trial') : t('active')}
              </span>
            </div>
            {isTrialing && currentSubscription?.trial_ends_at && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('trial-ends')}:</span>
                <span className="font-medium text-gray-800">
                  {new Date(
                    currentSubscription.trial_ends_at,
                  ).toLocaleDateString(i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="mb-8 w-full max-w-md">
          <h3 className="mb-4 text-xl font-semibold text-gray-800">
            {t('next-steps-title')}
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start">
              <div className="mr-3 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                <FaCheck className="size-3 text-green-500" />
              </div>
              <span className="text-gray-600">{t('step-1')}</span>
            </li>
            <li className="flex items-start">
              <div className="mr-3 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                <FaCheck className="size-3 text-green-500" />
              </div>
              <span className="text-gray-600">{t('step-2')}</span>
            </li>
            <li className="flex items-start">
              <div className="mr-3 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                <FaCheck className="size-3 text-green-500" />
              </div>
              <span className="text-gray-600">{t('step-3')}</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
          <Button
            onClick={() => navigate(`/${i18n.language}/dashboard`)}
            className="bg-linko-light text-white transition-colors duration-300 hover:bg-linko-dark"
          >
            {t('go-to-dashboard')}
          </Button>
          {user?.company && (
            <Button
              onClick={() =>
                navigate(`/${i18n.language}/dashboard/my-company?section=1`)
              }
              className="bg-linko-dark text-white transition-colors duration-300 hover:bg-linko-light"
            >
              {t('go-to-users')}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
